/**
 * Class Schedules tab — one card per assigned class SCHEDULE PATTERN
 * (class_id + schedule_id), never per generated occurrence. Backed by
 * GET /api/team/{staff}/class-schedules. Filters: Day (multi, match-any),
 * From/To Time (30-min interval overlap), Grade + Subject (grade-dependent,
 * exact grade→subject pair matching), Class Type, Study Mode, Location,
 * Enrollment, Search. No date filter — the tab intentionally never asks
 * for a date range.
 */
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import {
  Avatar, AvatarGroup, Box, Button, Checkbox, Chip, CircularProgress,
  Divider, FormControl, IconButton, InputLabel, ListItemText, MenuItem,
  Select, Stack, TextField, Tooltip, Typography, alpha, useTheme,
} from "@mui/material";
import { Loader, PageOverlayLoader } from "../../../../Components/Loader";
import ScheduleIcon from "@mui/icons-material/Schedule";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import LaptopMacOutlinedIcon from "@mui/icons-material/LaptopMacOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import GroupsIcon from "@mui/icons-material/Groups";
import RefreshIcon from "@mui/icons-material/Refresh";
import AddIcon from "@mui/icons-material/Add";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EventNoteIcon from "@mui/icons-material/EventNote";
import SearchIcon from "@mui/icons-material/Search";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import PersonRemoveOutlinedIcon from "@mui/icons-material/PersonRemoveOutlined";
import DashboardCustomizeOutlinedIcon from "@mui/icons-material/DashboardCustomizeOutlined";
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import EventSeatOutlinedIcon from "@mui/icons-material/EventSeatOutlined";
import PersonOffOutlinedIcon from "@mui/icons-material/PersonOffOutlined";
import RestartAltOutlinedIcon from "@mui/icons-material/RestartAltOutlined";
import TuneOutlinedIcon from "@mui/icons-material/TuneOutlined";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Menu } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { useGetStaffClassSchedulesQuery, useGetAllClassSchedulesQuery } from "../../../../Api/attoDeskApi";
import AddStudentDialog from "./ClassScheduleActions/AddStudentDialog";
import ReleaseStudentDialog from "./ClassScheduleActions/ReleaseStudentDialog";
import SwitchStaffDialog from "./ClassScheduleActions/SwitchStaffDialog";
import { useStudentProfilePath } from "../../../StaffPortal/shared/routes";

// ── Types ─────────────────────────────────────────────────────────────
interface GradeSubjects { grade_id: number; grade: string; subjects: { id: number; name: string }[] }
interface SchedulePatternStudent { id: number; name: string; grade?: string | null; image?: string | null }
export interface SchedulePattern {
  class_id: number; schedule_id: number;
  staff?: { id: number; name: string } | null;
  day: string; start_time: string; end_time: string;
  class_type: string | null; program: string | null; study_mode: string | null;
  location: { id: number; name: string | null } | null;
  grade_subjects: GradeSubjects[];
  capacity: number; enrolled: number; available: number | null;
  students: SchedulePatternStudent[];
  recurring: boolean; status: string;
  next_occurrence_id: number | null; next_occurrence_date: string | null;
  /** Recurring series start date (YYYY-MM-DD), or null if unknown. */
  series_start_date?: string | null;
  /** Recurring series end date (YYYY-MM-DD). Null = ongoing / no end date. */
  series_end_date?: string | null;
}
interface FilterOptions {
  days: { id: string; name: string }[];
  grades: { id: number; name: string }[];
  subjects_by_grade: { grade_id: number; grade: string; subjects: { id: number; name: string }[] }[];
  class_types: { id: string; name: string }[];
  study_modes: { id: string; name: string }[];
  locations: { id: number; name: string | null }[];
  staff?: { id: number; name: string }[];
  programs?: { id: string; name: string }[];
}

// ── Constants ─────────────────────────────────────────────────────────
const DAY_ORDER = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_LABELS: Record<string, string> = {
  monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday", thursday: "Thursday",
  friday: "Friday", saturday: "Saturday", sunday: "Sunday",
};
const ENROLLMENT_OPTIONS = [
  { id: "", name: "All Enrollments" },
  { id: "has_students", name: "Has Students" },
  { id: "no_students", name: "No Students" },
  { id: "full", name: "Full" },
  { id: "seats_available", name: "Seats Available" },
  { id: "over_capacity", name: "Over Capacity" },
];

// 30-minute interval time options between an inclusive start and end (HH:mm).
const makeTimeOptions = (startHour: number, endHour: number): string[] => {
  const out: string[] = [];
  for (let h = startHour; h <= endHour; h++) {
    for (const m of [0, 30]) {
      if (h === endHour && m > 0) break;
      out.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }
  return out;
};

const fmtTime12 = (t: string): string => {
  if (!t) return "";
  const [hStr, mStr] = t.split(":");
  let h = parseInt(hStr, 10); const m = parseInt(mStr, 10);
  if (Number.isNaN(h)) return t;
  const suffix = h >= 12 ? "PM" : "AM";
  h = h % 12; if (h === 0) h = 12;
  return `${h}:${String(m).padStart(2, "0")} ${suffix}`;
};

const STATUS_COLORS: Record<string, string> = {
  active: "#22c55e", upcoming: "#0ea5e9", completed: "#9ca3af", cancelled: "#ef4444",
};

// ══════════════════════════════════════════════════════════════════════
// Main component
// ══════════════════════════════════════════════════════════════════════
interface Props {
  /** When set, the tab is staff-scoped (used by the Staff Profile page).
   *  When undefined, the tab renders the GLOBAL class-schedules page —
   *  adds a Staff filter and shows the assigned staff per card. */
  staffId?: string;
  onAddClass?: () => void;
  canEditSchedule?: boolean;
  /** When true (global page), shows the KPI summary strip above filters. */
  showKpis?: boolean;
  /** Page title — omit to keep the tab in its compact "Class Schedules" header. */
  title?: string;
  subtitle?: string;
  /** Slot rendered in the hero header (only when `title` is set). Used by the
   *  global /class-schedules page to embed the Location Colours legend INSIDE
   *  the content card instead of floating above it. */
  headerAdornment?: React.ReactNode;
  /** When true, the tab hides itself behind the shared full-page <Loader />
   *  until the first data response arrives (success OR empty). This is for
   *  the standalone /class-schedules page, where the visitor should NOT see
   *  a shell with placeholder-zero KPIs, weekday counts, or "0 patterns"
   *  while the initial fetch is in flight. Staff Profile embeds this tab
   *  inside its own tabset and never sets this — its existing inline spinner
   *  behaviour stays. */
  useFullPageLoader?: boolean;
}

const ClassSchedulesTab: React.FC<Props> = ({ staffId, onAddClass, canEditSchedule, showKpis, title, subtitle, headerAdornment, useFullPageLoader }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const isGlobal = !staffId;
  // Route staff-role viewers to the Staff Portal profile (StudentView),
  // admins to /student-history. Staff hitting the admin route see "No
  // data available" because StudentHistory doesn't fetch staff-scoped data.
  const buildStudentProfilePath = useStudentProfilePath();

  // The Global page has a weekday-pill toolbar that must round-trip through
  // the URL (?day=monday) so a refresh/share/bookmark reopens on the same
  // day. Staff Profile's Class Schedules tab intentionally does not touch
  // the URL, so we only sync when isGlobal.
  const [searchParams, setSearchParams] = useSearchParams();

  // ── Filter state (URL not encoded — filter changes stay tab-local,
  //    except for `day` on the global page — see the effects below). ──
  const [selectedDays, setSelectedDays] = useState<string[]>(() => {
    if (!isGlobal) return [];
    const raw = (searchParams.get("day") || "").trim().toLowerCase();
    return DAY_ORDER.includes(raw) ? [raw] : [];
  });
  const [fromTime, setFromTime] = useState<string>("");
  const [toTime, setToTime] = useState<string>("");
  const [selectedStaff, setSelectedStaff] = useState<number[]>([]);
  const [selectedGrades, setSelectedGrades] = useState<number[]>([]);
  const [selectedSubjects, setSelectedSubjects] = useState<number[]>([]);
  const [selectedClassTypes, setSelectedClassTypes] = useState<string[]>([]);
  const [selectedPrograms, setSelectedPrograms] = useState<string[]>([]);
  const [selectedStudyModes, setSelectedStudyModes] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<number[]>([]);
  const [selectedStatuses, setSelectedStatuses] = useState<string[]>([]);
  const [enrollment, setEnrollment] = useState<string>("");
  const [search, setSearch] = useState<string>("");
  const [sort, setSort] = useState<string>("day_time");
  // Phase-1 server pagination for this page. 25 patterns/page. Every
  // filter/search/sort change resets to page 1 (effect below). URL sync
  // is single-source: `page` hydrates from `?page=` on mount, and every
  // subsequent change writes back to `?page=` — no duplicate state.
  const PER_PAGE = 25;
  const [page, setPage] = useState<number>(() => {
    if (!isGlobal) return 1;
    const raw = parseInt(searchParams.get("page") || "1", 10);
    return Number.isFinite(raw) && raw > 0 ? raw : 1;
  });

  // Bulk-action dialog state (spec §5–§8) — one active dialog at a time.
  const [addStudentFor, setAddStudentFor] = useState<SchedulePattern | null>(null);
  const [releaseStudentFor, setReleaseStudentFor] = useState<SchedulePattern | null>(null);
  const [switchStaffFor, setSwitchStaffFor] = useState<SchedulePattern | null>(null);

  // Client-side validation for the time window (spec §11).
  const timeError = fromTime && toTime && toTime <= fromTime
    ? "End time must be later than start time." : "";

  const sharedArgs = useMemo(() => ({
    days: selectedDays,
    from_time: fromTime || undefined,
    to_time: toTime || undefined,
    grade_ids: selectedGrades,
    subject_ids: selectedSubjects,
    class_types: selectedClassTypes,
    study_modes: selectedStudyModes,
    location_ids: selectedLocations,
    enrollment: enrollment || undefined,
    search: search || undefined,
    sort,
    page,
    per_page: PER_PAGE,
  }), [selectedDays, fromTime, toTime, selectedGrades, selectedSubjects,
       selectedClassTypes, selectedStudyModes, selectedLocations, enrollment, search, sort, page]);

  // Any filter / search / sort change: back to page 1. The dep array
  // deliberately excludes `page` so bumping the page doesn't reset itself.
  // The ref guard skips the initial-mount fire so a URL-restored `?page=3`
  // isn't clobbered back to 1 before the first fetch — spec §13 ("Do not
  // reset to page 1 when: The URL page parameter is restored").
  const filterResetGuardRef = React.useRef(false);
  React.useEffect(() => {
    if (!filterResetGuardRef.current) {
      filterResetGuardRef.current = true;
      return;
    }
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDays, fromTime, toTime, selectedStaff, selectedGrades, selectedSubjects,
      selectedClassTypes, selectedPrograms, selectedStudyModes, selectedLocations,
      selectedStatuses, enrollment, search, sort]);

  const skip = !!timeError;

  // Staff-scoped query (only fires when staffId is present + no time error).
  const staffQuery = useGetStaffClassSchedulesQuery(
    isGlobal || skip ? (skipToken as any) : { staffId: staffId as string, ...sharedArgs },
    { refetchOnMountOrArgChange: true },
  );
  // Global query (only fires in global mode + no time error).
  const globalQuery = useGetAllClassSchedulesQuery(
    !isGlobal || skip ? (skipToken as any) : {
      ...sharedArgs,
      staff_ids: selectedStaff,
      program_types: selectedPrograms,
      status: selectedStatuses,
    },
    { refetchOnMountOrArgChange: true },
  );
  const { data, isFetching, isLoading, isError, refetch } = isGlobal ? globalQuery : staffQuery;

  // ── First-load coordination ────────────────────────────────────────────
  // The one active query returns list + KPIs + weekday counts + filter
  // options + pagination in a single response. Track when the FIRST fetch
  // has produced any data (success OR empty) so the standalone page can
  // hide behind the shared full-page loader until the whole shell has real
  // numbers to render. Sticky: once true, filter/page changes don't flip
  // it back — only the results area shows the lightweight isFetching dim.
  const [hasEverLoaded, setHasEverLoaded] = useState(false);
  useEffect(() => {
    if (data !== undefined && !isFetching) setHasEverLoaded(true);
  }, [data, isFetching]);
  // Reset when the page unmounts+remounts (fresh visit) — happens naturally
  // because component state resets. No cleanup needed.

  const patterns: SchedulePattern[] = useMemo(
    () => (Array.isArray((data as any)?.data) ? (data as any).data : []),
    [data],
  );
  const filterOpts: FilterOptions = (data as any)?.filter_options ?? {
    days: [], grades: [], subjects_by_grade: [], class_types: [], study_modes: [], locations: [],
  };
  const totalSchedules = (data as any)?.summary?.total_schedules ?? patterns.length;

  // Auto-clamp: if the URL requested a page past the end of the current
  // result set (spec §19), the backend already returns the last page's
  // rows AND echoes back its own `current_page`. Snap local state to the
  // authoritative value so the footer, URL and controls agree. Guarded
  // by ref so this only fires when the mismatch is real, not on every
  // fresh render.
  const paginationMeta = (data as any)?.pagination;
  React.useEffect(() => {
    if (!paginationMeta) return;
    const authoritative = paginationMeta.current_page;
    if (typeof authoritative !== "number") return;
    if (authoritative !== page && !isFetching) {
      setPage(authoritative);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationMeta?.current_page, isFetching]);

  // Scroll the results back to the top after a successful pagination
  // change so the user lands on row 1 of the new page (spec §1). We wait
  // for isFetching to settle so the scroll fires against the freshly
  // rendered rows, not the still-loading overlay.
  const lastScrolledPageRef = React.useRef<number>(page);
  React.useEffect(() => {
    if (isFetching) return;
    if (page === lastScrolledPageRef.current) return;
    lastScrolledPageRef.current = page;
    const el = document.getElementById("staff-class-schedules-tab");
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [page, isFetching]);

  // ── Dynamic time-picker range: extend to cover the earliest/latest
  //    configured class time, but never contract below 6:00–22:00.
  const [timeMin, timeMax] = useMemo(() => {
    let minH = 6, maxH = 22;
    patterns.forEach((p) => {
      const sh = parseInt((p.start_time || "").split(":")[0] || "99", 10);
      const eh = parseInt((p.end_time || "").split(":")[0] || "0", 10);
      if (!Number.isNaN(sh) && sh < minH) minH = sh;
      if (!Number.isNaN(eh) && eh > maxH) maxH = eh;
    });
    return [Math.max(0, minH), Math.min(23, maxH)];
  }, [patterns]);
  const timeOptions = useMemo(() => makeTimeOptions(timeMin, timeMax), [timeMin, timeMax]);

  // Subject options depend on selected grades (spec §12). Union when multiple
  // grades selected; full list when none selected.
  const dependentSubjectOptions = useMemo(() => {
    const src = filterOpts.subjects_by_grade || [];
    const map = new Map<number, { id: number; name: string; grade: string }>();
    src.forEach((g) => {
      if (selectedGrades.length && !selectedGrades.includes(g.grade_id)) return;
      g.subjects.forEach((s) => { if (!map.has(s.id)) map.set(s.id, { ...s, grade: g.grade }); });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [filterOpts.subjects_by_grade, selectedGrades]);

  // Drop any selected subjects that are no longer valid after a grade change.
  React.useEffect(() => {
    if (!selectedSubjects.length) return;
    const valid = new Set(dependentSubjectOptions.map((s) => s.id));
    const next = selectedSubjects.filter((id) => valid.has(id));
    if (next.length !== selectedSubjects.length) setSelectedSubjects(next);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dependentSubjectOptions]);

  const resetFilters = () => {
    setSelectedDays([]); setFromTime(""); setToTime("");
    setSelectedStaff([]); setSelectedGrades([]); setSelectedSubjects([]);
    setSelectedClassTypes([]); setSelectedPrograms([]); setSelectedStudyModes([]); setSelectedLocations([]);
    setSelectedStatuses([]); setEnrollment(""); setSearch(""); setSort("day_time");
  };

  const anyFilterActive = !!(
    selectedDays.length || fromTime || toTime ||
    selectedStaff.length || selectedGrades.length || selectedSubjects.length ||
    selectedClassTypes.length || selectedPrograms.length || selectedStudyModes.length ||
    selectedLocations.length || selectedStatuses.length || enrollment || search
  );

  // Keep ?day=<name> in sync with the weekday-pill state (global page only).
  // Only writes when the value would actually change so a back-nav that also
  // preserves other query params doesn't get overwritten by every render.
  React.useEffect(() => {
    if (!isGlobal) return;
    const single = selectedDays.length === 1 ? selectedDays[0] : "";
    const cur = (searchParams.get("day") || "").toLowerCase();
    if (single === cur) return;
    const next = new URLSearchParams(searchParams);
    if (single) next.set("day", single); else next.delete("day");
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDays, isGlobal]);

  // Keep ?page=N in sync with the paginated state so refresh, share and
  // browser Back/Forward round-trip the current page — spec §5. Page 1
  // is treated as the default and stripped from the URL to keep the
  // canonical form short. Only writes on true change so paging doesn't
  // create a spurious history entry.
  React.useEffect(() => {
    if (!isGlobal) return;
    const cur = parseInt(searchParams.get("page") || "1", 10);
    if (page === cur || (page === 1 && !searchParams.has("page"))) return;
    const next = new URLSearchParams(searchParams);
    if (page > 1) next.set("page", String(page)); else next.delete("page");
    if (next.toString() !== searchParams.toString()) setSearchParams(next, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, isGlobal]);

  // Reflect browser Back/Forward: if the URL's ?page= changes underneath us
  // (history navigation, not a click on our own pagination buttons), pull
  // the new page into local state. Guarded by ref so the click path — which
  // updates state first, then URL — doesn't ping-pong.
  React.useEffect(() => {
    if (!isGlobal) return;
    const urlPage = parseInt(searchParams.get("page") || "1", 10);
    const clean = Number.isFinite(urlPage) && urlPage > 0 ? urlPage : 1;
    if (clean !== page) setPage(clean);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, isGlobal]);

  // Click helper: "All Days" → selectedDays=[], any weekday → selectedDays=[d].
  // Sits on the same state as the existing Day dropdown so the two never
  // diverge (a multi-day selection from the dropdown simply means no pill is
  // highlighted — the "All Days" pill only highlights when the array is
  // truly empty).
  const setDayPill = (day: string | "") => {
    if (day === "") { setSelectedDays([]); return; }
    setSelectedDays([day]);
  };

  const summary = (data as any)?.summary ?? {};

  // Group the visible page of patterns by weekday and sort each day's
  // patterns by start time. Renders as a compact grouped table below —
  // one day header per group, then a compact row per pattern.
  const groupedByDay = useMemo(() => {
    const groups = new Map<string, SchedulePattern[]>();
    patterns.forEach((p) => {
      const key = (p.day || "").toLowerCase();
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(p);
    });
    return DAY_ORDER
      .filter((d) => groups.has(d))
      .map((d) => {
        const rows = [...(groups.get(d) || [])].sort((a, b) =>
          (a.start_time || "").localeCompare(b.start_time || "")
        );
        return { day: d, label: DAY_LABELS[d] || d.toUpperCase(), rows };
      });
  }, [patterns]);

  // Row-level "More" menu — anchored per-row. `menuFor` holds the pattern
  // whose menu is open (or null). One menu across all rows keeps DOM tiny.
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuFor, setMenuFor] = useState<SchedulePattern | null>(null);
  const openRowMenu = (el: HTMLElement, p: SchedulePattern) => {
    setMenuAnchor(el); setMenuFor(p);
  };
  const closeRowMenu = () => { setMenuAnchor(null); setMenuFor(null); };

  // ── Full-page loader / initial error gate ─────────────────────────────
  // Only runs on the standalone /class-schedules page (useFullPageLoader).
  // Held down until the first fetch finishes, so KPIs, weekday counts, the
  // "0 patterns" chip and filter dropdowns never briefly render as zeros.
  if (useFullPageLoader && !hasEverLoaded) {
    if (isError) {
      return (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", minHeight: "60vh", gap: 1.5, textAlign: "center", px: 3 }}>
          <Typography sx={{ fontSize: 18, fontWeight: 700 }}>Couldn't load class schedules</Typography>
          <Typography sx={{ fontSize: 13, color: "text.secondary", maxWidth: 480 }}>
            The initial request failed. Check your connection, then try again.
          </Typography>
          <Button
            variant="contained"
            startIcon={<RefreshIcon sx={{ fontSize: 16 }} />}
            onClick={() => refetch()}
            disabled={isFetching}
            sx={{ mt: 1, textTransform: "none", borderRadius: "9px" }}
          >
            Retry
          </Button>
        </Box>
      );
    }
    // Success or in-flight — reuse the shared full-page loader used across
    // the app (Routes.tsx Suspense fallback), so this page is visually
    // consistent with the initial navigation loader.
    return <Loader />;
  }

  return (
    <Box id="staff-class-schedules-tab" sx={{
      // position: relative anchors the PageOverlayLoader at the bottom
      // of this card so its inset:0 spans the KPIs, filters, weekday
      // buttons, results and pagination — exactly the "page content"
      // scope the spec calls out. The app sidebar + top header live
      // outside this Box and stay visible.
      position: "relative",
      bgcolor: "#fff", borderRadius: "16px", border: "1px solid rgba(15,23,42,0.06)",
      boxShadow: "0 1px 3px rgba(15,23,42,0.03), 0 12px 40px -24px rgba(15,23,42,0.12)", overflow: "hidden",
    }}>
      {/* ── Premium hero (global page) — title, subtitle and the Location
             Colours legend live INSIDE the card so nothing floats above it. */}
      {(title || subtitle || headerAdornment) && (
        <Box sx={{
          px: { xs: 2, md: 3 }, pt: { xs: 2.25, md: 2.75 }, pb: { xs: 1.75, md: 2.25 },
          borderBottom: "1px solid rgba(15,23,42,0.05)",
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.08)} 0%, ${alpha("#0ea5e9", 0.05)} 55%, rgba(255,255,255,0) 100%)`,
        }}>
          <Box sx={{
            display: "flex", flexDirection: { xs: "column", md: "row" },
            alignItems: { xs: "flex-start", md: "center" }, justifyContent: "space-between",
            gap: { xs: 1.5, md: 2 },
          }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, minWidth: 0 }}>
              <Box sx={{
                width: 40, height: 40, borderRadius: "12px", flexShrink: 0,
                display: "flex", alignItems: "center", justifyContent: "center",
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.7)} 100%)`,
                color: "#fff", boxShadow: `0 6px 16px -6px ${alpha(theme.palette.primary.main, 0.6)}`,
              }}>
                <DashboardCustomizeOutlinedIcon sx={{ fontSize: 20 }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                {title && (
                  <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 800, letterSpacing: "-0.02em", lineHeight: 1.15 }}>
                    {title}
                  </Typography>
                )}
                {subtitle && (
                  <Typography sx={{ fontSize: 13, color: "text.secondary", mt: 0.25, maxWidth: 720 }}>
                    {subtitle}
                  </Typography>
                )}
              </Box>
            </Box>
            {headerAdornment && (
              <Box sx={{ flexShrink: 0, maxWidth: "100%" }}>
                {headerAdornment}
              </Box>
            )}
          </Box>
        </Box>
      )}

      {/* ── KPI summary strip (global page) — premium colored tiles ── */}
      {showKpis && (
        <Box sx={{
          display: "grid", gap: { xs: 1.25, md: 1.75 }, px: { xs: 1.5, md: 3 }, py: { xs: 2, md: 2.5 },
          gridTemplateColumns: { xs: "repeat(2, minmax(0,1fr))", sm: "repeat(3, minmax(0,1fr))", lg: "repeat(6, minmax(0,1fr))" },
          borderBottom: "1px solid rgba(15,23,42,0.05)",
          bgcolor: alpha("#f8fafc", 0.6),
        }}>
          <KpiTile label="Total Schedules" value={summary.total_patterns ?? patterns.length} color="#6366f1" icon={<ScheduleIcon sx={{ fontSize: 18 }} />} />
          <KpiTile label="Matched" value={summary.matched ?? patterns.length} color="#0ea5e9" icon={<CheckCircleOutlineIcon sx={{ fontSize: 18 }} />} />
          <KpiTile label="Active Students" value={summary.active_students ?? 0} color="#22c55e" icon={<GroupsIcon sx={{ fontSize: 18 }} />} />
          <KpiTile label="Assigned Staff" value={summary.assigned_staff ?? 0} color="#f59e0b" icon={<BadgeOutlinedIcon sx={{ fontSize: 18 }} />} />
          <KpiTile label="Seats Available" value={summary.seats_available ?? 0} color="#8b5cf6" icon={<EventSeatOutlinedIcon sx={{ fontSize: 18 }} />} />
          <KpiTile label="No-Student Schedules" value={summary.no_student_schedules ?? 0} color="#ec4899"
            icon={<PersonOffOutlinedIcon sx={{ fontSize: 18 }} />}
            onClick={() => setEnrollment("no_students")} />
        </Box>
      )}

      {/* ── Toolbar header ── */}
      <Box sx={{
        px: { xs: 2, md: 3 }, py: 1.75, borderBottom: "1px solid rgba(15,23,42,0.05)",
        display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1,
      }}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Box sx={{ color: theme.palette.primary.main, display: "flex", "& svg": { fontSize: 18 } }}>
            <ScheduleIcon />
          </Box>
          <Typography sx={{ fontSize: 14, fontWeight: 800, letterSpacing: "-0.01em" }}>
            Class Schedules
          </Typography>
          <Chip
            size="small"
            label={`${totalSchedules} pattern${totalSchedules === 1 ? "" : "s"}`}
            sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main, border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}` }}
          />
        </Stack>
        <Stack direction="row" spacing={0.75}>
          <Tooltip title="Refresh">
            <IconButton size="small" onClick={() => refetch()} disabled={isFetching}
              sx={{ width: 32, height: 32, border: "1px solid rgba(15,23,42,0.1)", borderRadius: "9px", bgcolor: "#fff",
                "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.06), borderColor: alpha(theme.palette.primary.main, 0.3) } }}>
              <RefreshIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          {onAddClass && (
            <Button size="small" variant="contained" startIcon={<AddIcon sx={{ fontSize: "14px !important" }} />}
              onClick={onAddClass}
              sx={{
                textTransform: "none", fontSize: 12, fontWeight: 700, height: 32,
                boxShadow: `0 6px 14px -6px ${alpha(theme.palette.primary.main, 0.5)}`,
                borderRadius: "9px", px: 1.75,
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${alpha(theme.palette.primary.main, 0.85)} 100%)`,
                "&:hover": { boxShadow: `0 8px 18px -6px ${alpha(theme.palette.primary.main, 0.6)}` },
              }}>
              Add Class
            </Button>
          )}
        </Stack>
      </Box>

      {/* ── Filters card ── */}
      <Box sx={{ px: { xs: 2, md: 3 }, py: 2.25, borderBottom: "1px solid rgba(15,23,42,0.04)", bgcolor: alpha("#f8fafc", 0.7) }}>
        <Stack direction="row" alignItems="center" spacing={0.75} sx={{ mb: 1.5 }}>
          <FilterAltOutlinedIcon sx={{ fontSize: 15, color: "text.disabled" }} />
          <Typography sx={{ fontSize: 11, fontWeight: 800, color: "text.disabled", textTransform: "uppercase", letterSpacing: 0.6 }}>
            Filters
          </Typography>
        </Stack>
        <Box sx={{
          display: "grid", gap: { xs: 1.25, md: 1.5 },
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0,1fr))", md: "repeat(3, minmax(0,1fr))", lg: "repeat(6, minmax(0,1fr))" },
          alignItems: "stretch",
          "& .MuiOutlinedInput-root": { bgcolor: "#fff", borderRadius: "10px" },
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(15,23,42,0.12)" },
        }}>
          <TextField
            size="small" placeholder="Search schedules…" value={search}
            onChange={(e) => setSearch(e.target.value)}
            InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 17, color: "text.disabled", mr: 0.75 }} /> }}
          />
          <MultiSelect
            label="Day"
            options={(filterOpts.days.length ? filterOpts.days : DAY_ORDER.map((d) => ({ id: d, name: DAY_LABELS[d] })))}
            value={selectedDays}
            onChange={setSelectedDays}
            summary={(vals) => vals.length === 0 ? "All Days" : vals.length === 1 ? DAY_LABELS[vals[0]] || vals[0] : `Days · ${vals.length}`}
          />
          <TimeSelect label="From Time" value={fromTime} onChange={setFromTime} options={timeOptions} />
          <TimeSelect label="To Time" value={toTime} onChange={setToTime} options={timeOptions} error={!!timeError} />
          {isGlobal && (
            <MultiSelect
              label="Staff"
              options={(filterOpts.staff || []).map((s) => ({ id: String(s.id), name: s.name }))}
              value={selectedStaff.map(String)}
              onChange={(vals) => setSelectedStaff(vals.map(Number))}
              summary={(vals) => vals.length === 0 ? "All Staff" : vals.length === 1 ? ((filterOpts.staff || []).find((s) => String(s.id) === vals[0])?.name || vals[0]) : `Staff · ${vals.length}`}
            />
          )}
          <MultiSelect
            label="Grade"
            options={filterOpts.grades.map((g) => ({ id: String(g.id), name: g.name }))}
            value={selectedGrades.map(String)}
            onChange={(vals) => setSelectedGrades(vals.map(Number))}
            summary={(vals) => vals.length === 0 ? "All Grades" : vals.length === 1 ? (filterOpts.grades.find((g) => String(g.id) === vals[0])?.name || vals[0]) : `Grades · ${vals.length}`}
          />
          <MultiSelect
            label="Subject"
            options={dependentSubjectOptions.map((s) => ({ id: String(s.id), name: s.name }))}
            value={selectedSubjects.map(String)}
            onChange={(vals) => setSelectedSubjects(vals.map(Number))}
            summary={(vals) => vals.length === 0 ? "All Subjects" : vals.length === 1 ? (dependentSubjectOptions.find((s) => String(s.id) === vals[0])?.name || vals[0]) : `Subjects · ${vals.length}`}
            disabled={dependentSubjectOptions.length === 0}
          />
        </Box>
        {timeError && (
          <Typography sx={{ mt: 0.75, fontSize: 11, color: "#ef4444" }}>{timeError}</Typography>
        )}

        <Box sx={{
          mt: { xs: 1.25, md: 1.5 }, display: "grid", gap: { xs: 1.25, md: 1.5 },
          gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0,1fr))", md: "repeat(3, minmax(0,1fr))", lg: "repeat(6, minmax(0,1fr))" },
          alignItems: "stretch",
          "& .MuiOutlinedInput-root": { bgcolor: "#fff", borderRadius: "10px" },
          "& .MuiOutlinedInput-notchedOutline": { borderColor: "rgba(15,23,42,0.12)" },
        }}>
          <MultiSelect
            label="Class Type"
            options={filterOpts.class_types}
            value={selectedClassTypes}
            onChange={setSelectedClassTypes}
            summary={(vals) => vals.length === 0 ? "All Class Types" : vals.length === 1 ? vals[0] : `Types · ${vals.length}`}
          />
          {isGlobal && (filterOpts.programs?.length ?? 0) > 0 && (
            <MultiSelect
              label="Program"
              options={filterOpts.programs || []}
              value={selectedPrograms}
              onChange={setSelectedPrograms}
              summary={(vals) => vals.length === 0 ? "All Programs" : vals.length === 1 ? vals[0] : `Programs · ${vals.length}`}
            />
          )}
          <MultiSelect
            label="Study Mode"
            options={filterOpts.study_modes}
            value={selectedStudyModes}
            onChange={setSelectedStudyModes}
            summary={(vals) => vals.length === 0 ? "All Modes" : vals.length === 1 ? vals[0] : `Modes · ${vals.length}`}
          />
          {isGlobal && (
            <MultiSelect
              label="Status"
              options={[
                { id: "active", name: "Active" },
                { id: "upcoming", name: "Upcoming" },
                { id: "completed", name: "Completed" },
                { id: "cancelled", name: "Cancelled" },
              ]}
              value={selectedStatuses}
              onChange={setSelectedStatuses}
              summary={(vals) => vals.length === 0 ? "All Statuses" : vals.length === 1 ? vals[0] : `Statuses · ${vals.length}`}
            />
          )}
          <MultiSelect
            label="Location"
            options={filterOpts.locations.filter((l) => l.name).map((l) => ({ id: String(l.id), name: l.name as string }))}
            value={selectedLocations.map(String)}
            onChange={(vals) => setSelectedLocations(vals.map(Number))}
            summary={(vals) => vals.length === 0 ? "All Locations" : vals.length === 1 ? (filterOpts.locations.find((l) => String(l.id) === vals[0])?.name || vals[0]) : `Locations · ${vals.length}`}
          />
          <FormControl size="small">
            <InputLabel>Enrollment</InputLabel>
            <Select label="Enrollment" value={enrollment} onChange={(e) => setEnrollment(String(e.target.value))}>
              {ENROLLMENT_OPTIONS.map((o) => (
                <MenuItem key={o.id || "all"} value={o.id}>{o.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small">
            <InputLabel>Sort</InputLabel>
            <Select label="Sort" value={sort} onChange={(e) => setSort(String(e.target.value))}>
              <MenuItem value="day_time">Day & Time</MenuItem>
              <MenuItem value="earliest_time">Earliest Time</MenuItem>
              <MenuItem value="highest_enrollment">Highest Enrollment</MenuItem>
              <MenuItem value="most_available_seats">Most Seats Available</MenuItem>
              <MenuItem value="staff">Staff</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* ── Filter footer — active-filter summary + Reset — intentionally
               outside the grid so the control columns stay perfectly aligned
               regardless of which conditional filters are present. */}
        <Box sx={{
          mt: 1.75, pt: 1.5, borderTop: "1px dashed rgba(15,23,42,0.08)",
          display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between", gap: 1,
        }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ color: "text.secondary" }}>
            <TuneOutlinedIcon sx={{ fontSize: 15 }} />
            <Typography sx={{ fontSize: 12 }}>
              {anyFilterActive ? "Filters applied — narrowed results" : "No filters applied — showing all matching schedules"}
            </Typography>
          </Stack>
          <Button
            size="small" variant="outlined" onClick={resetFilters} disabled={!anyFilterActive}
            startIcon={<RestartAltOutlinedIcon sx={{ fontSize: "15px !important" }} />}
            sx={{
              textTransform: "none", fontSize: 12, fontWeight: 700, borderRadius: "9px",
              height: 32, px: 1.5, borderColor: "rgba(15,23,42,0.12)", color: "text.primary",
              bgcolor: "#fff",
              "&:hover": { borderColor: alpha(theme.palette.primary.main, 0.4), bgcolor: alpha(theme.palette.primary.main, 0.04) },
              "&.Mui-disabled": { color: "text.disabled" },
            }}>
            Reset Filters
          </Button>
        </Box>
      </Box>

      {/* ── Body ── */}
      <Box sx={{ p: { xs: 1.5, md: 2 } }}>
        {/* Weekday pill bar — shortcuts that write into the same
            selectedDays state as the "Day" dropdown. Only one weekday can be
            selected via the pills at a time (the dropdown remains multi-
            select); "All Days" is highlighted when selectedDays is empty. */}
        <WeekdayTabs
          activeDay={selectedDays.length === 1 ? selectedDays[0] : ""}
          multiActive={selectedDays.length > 1}
          countsByDay={(data as any)?.day_counts?.by_day || {}}
          allCount={(data as any)?.day_counts?.all}
          disabled={isFetching}
          onChange={setDayPill}
        />

        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress size={22} />
          </Box>
        ) : patterns.length === 0 ? (
          <EmptyState anyFilter={anyFilterActive} onClear={resetFilters} onAdd={onAddClass}
            onViewAll={() => navigate("/class-schedule/list")} />
        ) : (
          <Box sx={{ opacity: isFetching ? 0.75 : 1, transition: "opacity .15s" }}>
            {groupedByDay.map((group) => (
              <Box key={group.day} sx={{ mb: 2 }}>
                {/* Day header — subtle sticky group label so the user always
                    knows which weekday the visible rows belong to. */}
                <Box sx={{
                  position: "sticky", top: 0, zIndex: 1,
                  display: "flex", alignItems: "center", justifyContent: "space-between",
                  px: 1.5, py: 0.75, mb: 0.5,
                  bgcolor: alpha(theme.palette.primary.main, 0.06),
                  borderLeft: `3px solid ${theme.palette.primary.main}`,
                  borderRadius: "6px",
                }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", color: theme.palette.primary.main }}>
                    {group.label}
                  </Typography>
                  <Typography sx={{ fontSize: 11, color: "text.secondary", fontWeight: 600 }}>
                    {group.rows.length} class{group.rows.length === 1 ? "" : "es"}
                  </Typography>
                </Box>

                {/* Compact row list. Each row is a single-height flex line;
                    student chips wrap to a second line on narrow widths. */}
                <Box sx={{ display: "flex", flexDirection: "column", border: "1px solid rgba(15,23,42,0.06)", borderRadius: "10px", bgcolor: "#fff", overflow: "hidden" }}>
                  {group.rows.map((p, idx) => (
                    <ScheduleTableRow
                      key={`${p.class_id}-${p.schedule_id}`}
                      pattern={p}
                      isFirst={idx === 0}
                      showStaff={isGlobal}
                      onViewClass={() => navigate(`/class-schedules/${p.class_id}`)}
                      onEditSchedule={canEditSchedule
                        ? () => navigate(`/classes/${p.class_id}/edit?return_url=${encodeURIComponent(location.pathname + location.search)}`)
                        : undefined}
                      onOpenMenu={(el) => openRowMenu(el, p)}
                      onStudentClick={(sid) => navigate(buildStudentProfilePath(sid))}
                    />
                  ))}
                </Box>
              </Box>
            ))}
          </Box>
        )}

        {/* Row-level "More" menu — secondary actions live here instead of
            crowding every row with six buttons. Populated from menuFor. */}
        <Menu
          anchorEl={menuAnchor}
          open={!!menuAnchor && !!menuFor}
          onClose={closeRowMenu}
          anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
          transformOrigin={{ vertical: "top", horizontal: "right" }}
          slotProps={{ paper: { sx: { minWidth: 210, borderRadius: "10px", mt: 0.5 } } }}
        >
          {menuFor?.next_occurrence_id && (
            <MenuItem onClick={() => {
              const p = menuFor; closeRowMenu();
              if (p?.next_occurrence_id) navigate(`/class-sessions/${p.next_occurrence_id}/edit?return_url=${encodeURIComponent(location.pathname + location.search)}`);
            }}>
              <EventNoteIcon sx={{ fontSize: 16, mr: 1 }} /> View next session
            </MenuItem>
          )}
          <MenuItem onClick={() => {
            const p = menuFor; closeRowMenu();
            if (p) navigate(`/class-sessions/list?class_id=${p.class_id}&schedule_id=${p.schedule_id}`);
          }}>
            <EventNoteIcon sx={{ fontSize: 16, mr: 1 }} /> View sessions
          </MenuItem>
          {canEditSchedule && (
            <MenuItem onClick={() => { const p = menuFor; closeRowMenu(); if (p) setAddStudentFor(p); }}>
              <AddIcon sx={{ fontSize: 16, mr: 1 }} /> Add student
            </MenuItem>
          )}
          {canEditSchedule && menuFor && menuFor.students.length > 0 && (
            <MenuItem onClick={() => { const p = menuFor; closeRowMenu(); if (p) setReleaseStudentFor(p); }} sx={{ color: "#dc2626" }}>
              <PersonRemoveOutlinedIcon sx={{ fontSize: 16, mr: 1 }} /> Release student
            </MenuItem>
          )}
          {canEditSchedule && (
            <MenuItem onClick={() => { const p = menuFor; closeRowMenu(); if (p) setSwitchStaffFor(p); }}>
              <SwapHorizIcon sx={{ fontSize: 16, mr: 1 }} /> Switch staff
            </MenuItem>
          )}
        </Menu>

        {/* Phase-1 pagination footer — server-side. Only rendered when
            there are results and more than one page. The page total is
            authoritative (matches the "Matched" chip); the visible slice
            is 25 by default. */}
        {(() => {
          const pg = (data as any)?.pagination;
          if (!pg || pg.total <= pg.per_page) return null;
          const lastPage: number = pg.last_page || 1;
          const cur: number = pg.current_page || 1;
          return (
            <Box sx={{
              display: "flex", flexWrap: "wrap", alignItems: "center", justifyContent: "space-between",
              gap: 1.5, mt: 2.5, pt: 2, borderTop: "1px dashed rgba(15,23,42,0.08)",
            }}>
              <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
                Showing {pg.from}–{pg.to} of {pg.total} schedules
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Button size="small" variant="outlined" disabled={!pg.has_previous_page || isFetching}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  sx={{ textTransform: "none", borderRadius: "8px" }}>Previous</Button>
                <Typography sx={{ fontSize: 12, color: "text.secondary", px: 0.5 }}>
                  Page {cur} of {lastPage}
                </Typography>
                <Button size="small" variant="outlined" disabled={!pg.has_next_page || isFetching}
                  onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                  sx={{ textTransform: "none", borderRadius: "8px" }}>Next</Button>
              </Stack>
            </Box>
          );
        })()}
      </Box>

      {/* ── Bulk-action dialogs (spec §5–§8) ── */}
      {addStudentFor && (
        <AddStudentDialog open pattern={addStudentFor} onClose={() => setAddStudentFor(null)} onDone={() => { setAddStudentFor(null); refetch(); }} />
      )}
      {releaseStudentFor && (
        <ReleaseStudentDialog open pattern={releaseStudentFor} onClose={() => setReleaseStudentFor(null)} onDone={() => { setReleaseStudentFor(null); refetch(); }} />
      )}
      {switchStaffFor && (
        <SwitchStaffDialog open pattern={switchStaffFor} onClose={() => setSwitchStaffFor(null)} onDone={() => { setSwitchStaffFor(null); refetch(); }} />
      )}

      {/* Branded Step Up Academy content-overlay loader. Any pending list
          refetch (filter change, weekday click, pagination, page-size,
          debounced search, sort, Reset Filters, Refresh) toggles this
          overlay so users see the same logo loader as the initial route
          load — not just the top progress bar. Sidebar + header stay
          visible because this Box is contained. */}
      <PageOverlayLoader visible={isFetching && !isLoading} size="large" />
    </Box>
  );
};

// ══════════════════════════════════════════════════════════════════════
// KPI tile (compact clickable summary)
// ══════════════════════════════════════════════════════════════════════
const KpiTile: React.FC<{ label: string; value: number | string; color: string; icon?: React.ReactNode; onClick?: () => void }> = ({ label, value, color, icon, onClick }) => (
  <Box
    component={onClick ? "button" : "div"}
    type={onClick ? "button" : undefined}
    onClick={onClick}
    sx={{
      textAlign: "left", cursor: onClick ? "pointer" : "default", font: "inherit",
      position: "relative", overflow: "hidden",
      bgcolor: "#fff", borderRadius: "14px", border: "1px solid rgba(15,23,42,0.06)",
      p: 1.75, display: "flex", alignItems: "center", gap: 1.25,
      boxShadow: "0 1px 2px rgba(15,23,42,0.03)",
      transition: "transform .15s ease, box-shadow .15s ease, border-color .15s",
      "&::before": {
        content: '""', position: "absolute", inset: 0, height: 3, top: 0,
        background: `linear-gradient(90deg, ${color} 0%, ${alpha(color, 0.4)} 100%)`,
      },
      "&:hover": onClick
        ? { transform: "translateY(-1px)", boxShadow: `0 10px 24px -12px ${alpha(color, 0.45)}`, borderColor: alpha(color, 0.3) }
        : {},
    }}
  >
    <Box sx={{
      width: 36, height: 36, borderRadius: "10px", flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center",
      bgcolor: alpha(color, 0.12), color,
    }}>
      {icon}
    </Box>
    <Box sx={{ minWidth: 0, display: "flex", flexDirection: "column", gap: 0.25 }}>
      <Typography sx={{ fontSize: 22, fontWeight: 800, color, letterSpacing: "-0.02em", lineHeight: 1 }}>{value}</Typography>
      <Typography sx={{ fontSize: 10.75, color: "text.secondary", fontWeight: 600, textTransform: "uppercase", letterSpacing: 0.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
        {label}
      </Typography>
    </Box>
  </Box>
);

// ══════════════════════════════════════════════════════════════════════
// Sub-components
// ══════════════════════════════════════════════════════════════════════

const MultiSelect: React.FC<{
  label: string;
  options: { id: string | number; name: string }[];
  value: string[];
  onChange: (v: string[]) => void;
  summary: (v: string[]) => string;
  disabled?: boolean;
}> = ({ label, options, value, onChange, summary, disabled }) => (
  <FormControl size="small" disabled={disabled}>
    <InputLabel>{label}</InputLabel>
    <Select
      multiple label={label} value={value}
      onChange={(e) => onChange(typeof e.target.value === "string" ? e.target.value.split(",") : (e.target.value as string[]))}
      renderValue={(vals) => summary(vals as string[])}
      MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}
    >
      {options.length === 0 && <MenuItem disabled>No options</MenuItem>}
      {options.map((o) => {
        const v = String(o.id);
        return (
          <MenuItem key={v} value={v}>
            <Checkbox size="small" checked={value.includes(v)} sx={{ p: 0.5, mr: 1 }} />
            <ListItemText primary={o.name} primaryTypographyProps={{ fontSize: 13 }} />
          </MenuItem>
        );
      })}
      {options.length > 0 && (
        <Box sx={{ borderTop: "1px solid rgba(0,0,0,0.06)", display: "flex", justifyContent: "space-between", px: 1, py: 0.5 }}>
          <Button size="small" onClick={() => onChange(options.map((o) => String(o.id)))} sx={{ fontSize: 11, textTransform: "none" }}>Select All</Button>
          <Button size="small" onClick={() => onChange([])} sx={{ fontSize: 11, textTransform: "none", color: "text.secondary" }}>Clear</Button>
        </Box>
      )}
    </Select>
  </FormControl>
);

const TimeSelect: React.FC<{
  label: string; value: string; onChange: (v: string) => void; options: string[]; error?: boolean;
}> = ({ label, value, onChange, options, error }) => (
  <FormControl size="small" error={error}>
    <InputLabel>{label}</InputLabel>
    <Select label={label} value={value} onChange={(e) => onChange(String(e.target.value))}
      MenuProps={{ PaperProps: { sx: { maxHeight: 320 } } }}>
      <MenuItem value=""><em>Any</em></MenuItem>
      {options.map((t) => <MenuItem key={t} value={t}>{fmtTime12(t)}</MenuItem>)}
    </Select>
  </FormControl>
);

/**
 * Weekday pill bar above the schedule list. Renders one button per weekday
 * plus "All Days". Each button carries a count badge showing how many
 * schedules match the OTHER active filters on that day, computed backend-
 * side so switching days doesn't re-run seven queries. Clicks flow through
 * the parent's setDayPill so the pills and the "Day" dropdown share state.
 */
const WeekdayTabs: React.FC<{
  activeDay: string;              // "" when "All Days"
  multiActive: boolean;           // true when the dropdown holds 2+ days (no pill highlights)
  countsByDay: Record<string, number>;
  allCount?: number;
  disabled?: boolean;
  onChange: (day: string | "") => void;
}> = ({ activeDay, multiActive, countsByDay, allCount, disabled, onChange }) => {
  const theme = useTheme();
  const pill = (isActive: boolean) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: 0.5,
    px: 1.5,
    py: 0.6,
    borderRadius: "999px",
    fontSize: 12,
    fontWeight: 700,
    textTransform: "none" as const,
    lineHeight: 1.2,
    border: `1px solid ${isActive ? theme.palette.primary.main : "rgba(15,23,42,0.1)"}`,
    bgcolor: isActive ? theme.palette.primary.main : "#fff",
    color: isActive ? "#fff" : "text.primary",
    boxShadow: isActive ? `0 4px 12px -6px ${alpha(theme.palette.primary.main, 0.5)}` : "none",
    minHeight: 32,
    "&:hover": { bgcolor: isActive ? theme.palette.primary.dark : alpha(theme.palette.primary.main, 0.06) },
    "&.Mui-disabled": { opacity: 0.6 },
    // Compact focus ring — never rely on colour alone (a11y).
    "&:focus-visible": { outline: `2px solid ${theme.palette.primary.main}`, outlineOffset: 2 },
  });
  const badgeSx = (isActive: boolean) => ({
    ml: 0.25,
    minWidth: 22,
    height: 18,
    px: 0.75,
    borderRadius: "9px",
    fontSize: 10.5,
    fontWeight: 800,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    bgcolor: isActive ? alpha("#fff", 0.22) : alpha(theme.palette.primary.main, 0.1),
    color: isActive ? "#fff" : theme.palette.primary.main,
  });
  const days = [
    { id: "", label: "All Days", count: allCount },
    ...DAY_ORDER.map((d) => ({ id: d, label: DAY_LABELS[d] || d, count: countsByDay[d] ?? 0 })),
  ];

  return (
    <Box
      role="tablist"
      aria-label="Filter schedules by weekday"
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.75,
        mb: 1.5,
        flexWrap: { xs: "nowrap", md: "wrap" },
        overflowX: { xs: "auto", md: "visible" },
        pb: { xs: 0.5, md: 0 },
        "::-webkit-scrollbar": { height: 4 },
      }}
    >
      {days.map((d) => {
        const isActive = !multiActive && activeDay === d.id;
        return (
          <Button
            key={d.id || "all"}
            role="tab"
            aria-pressed={isActive}
            aria-label={d.label + (typeof d.count === "number" ? ` · ${d.count}` : "")}
            disabled={disabled}
            onClick={() => onChange(d.id)}
            disableRipple={false}
            sx={pill(isActive)}
          >
            {d.label}
            {typeof d.count === "number" && (
              <Box component="span" sx={badgeSx(isActive)}>{d.count}</Box>
            )}
          </Button>
        );
      })}
    </Box>
  );
};

/**
 * Compact row for the redesigned Class Schedules table. One horizontal
 * line per schedule pattern, grouped under a day header by the parent.
 * Two primary actions (View + Edit) live inline; everything else is one
 * click deeper via the kebab in the last column.
 */
const ScheduleTableRow: React.FC<{
  pattern: SchedulePattern;
  isFirst: boolean;
  showStaff?: boolean;
  onViewClass: () => void;
  onEditSchedule?: () => void;
  onOpenMenu: (el: HTMLElement) => void;
  onStudentClick: (studentId: number) => void;
}> = ({ pattern, isFirst, showStaff, onViewClass, onEditSchedule, onOpenMenu, onStudentClick }) => {
  const theme = useTheme();
  const status = (pattern.status || "").toLowerCase();
  const statusColor = STATUS_COLORS[status] || "#6b7280";
  const capacity = pattern.capacity || 0;
  const enrolled = pattern.enrolled || 0;
  const overCapacity = capacity > 0 && enrolled > capacity;
  const isFull = capacity > 0 && enrolled === capacity;
  const capacityLabel = capacity > 0 ? `${enrolled} / ${capacity}` : `${enrolled}`;
  const capacityColor = overCapacity ? "#dc2626" : isFull ? "#f59e0b" : enrolled === 0 ? "#9ca3af" : "#22c55e";
  const preview = pattern.students.slice(0, 3);
  const overflow = Math.max(0, pattern.students.length - preview.length);
  const subjects = pattern.grade_subjects.flatMap((g) => g.subjects.map((s) => `${g.grade} · ${s.name}`));
  const subjectSummary = subjects.length === 0
    ? "No grade/subject configured"
    : subjects.length <= 2 ? subjects.join(" · ") : `${subjects.slice(0, 2).join(" · ")} · +${subjects.length - 2}`;

  return (
    <Box
      role="button"
      onClick={onViewClass}
      sx={{
        display: "grid",
        gridTemplateColumns: {
          xs: "minmax(0, 1fr) auto",
          md: "104px minmax(0, 2fr) minmax(0, 2.2fr) 84px auto",
        },
        alignItems: "center",
        columnGap: 1.5,
        rowGap: 0.5,
        px: { xs: 1.25, md: 1.5 },
        py: 1.25,
        cursor: "pointer",
        borderTop: isFirst ? "none" : "1px solid rgba(15,23,42,0.05)",
        transition: "background-color .12s",
        "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.03) },
      }}
    >
      {/* Time */}
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: theme.palette.primary.main, lineHeight: 1.2, letterSpacing: "-0.01em" }}>
          {fmtTime12(pattern.start_time)}
        </Typography>
        <Typography sx={{ fontSize: 11, color: "text.disabled", lineHeight: 1.2 }}>
          {fmtTime12(pattern.end_time)}
        </Typography>
      </Box>

      {/* Class details — subject/grade + staff + location/mode/type */}
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, color: "text.primary", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {subjectSummary}
        </Typography>
        <Stack direction="row" spacing={1} sx={{ mt: 0.4, flexWrap: "wrap", rowGap: 0.25, alignItems: "center" }}>
          {showStaff && pattern.staff && (
            <Typography sx={{ fontSize: 11.5, color: "text.secondary", fontWeight: 600 }}>{pattern.staff.name}</Typography>
          )}
          {pattern.location?.name && pattern.study_mode !== "Online" && (
            <Chip size="small" label={pattern.location.name} sx={{ height: 18, fontSize: 10.5, bgcolor: alpha("#0ea5e9", 0.09), color: "#0369a1" }} />
          )}
          {pattern.study_mode && (
            <Chip size="small" label={pattern.study_mode} sx={{ height: 18, fontSize: 10.5, bgcolor: alpha("#8b5cf6", 0.1), color: "#6d28d9" }} />
          )}
          {pattern.class_type && (
            <Chip size="small" label={`${pattern.class_type}${pattern.recurring ? " · Recurring" : " · One-off"}`}
              sx={{ height: 18, fontSize: 10.5, bgcolor: alpha("#64748b", 0.1), color: "#334155" }} />
          )}
          <Chip
            size="small"
            label={status.charAt(0).toUpperCase() + status.slice(1) || "Active"}
            sx={{
              height: 18, fontSize: 10.5, fontWeight: 700,
              bgcolor: alpha(statusColor, 0.1), color: statusColor,
            }}
          />
        </Stack>
      </Box>

      {/* Student preview — up to 3 name chips, "+N more" opens the details
          drawer (via View) so we never fetch a full roster inline. */}
      <Box sx={{ minWidth: 0, display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
        {enrolled === 0 ? (
          <Chip size="small" label="No students" sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: alpha("#ef4444", 0.08), color: "#b91c1c" }} />
        ) : (
          <>
            {preview.map((s) => (
              <Chip
                key={s.id}
                size="small"
                label={s.name}
                clickable
                onClick={(e) => { e.stopPropagation(); onStudentClick(s.id); }}
                sx={{ height: 20, fontSize: 10.5, bgcolor: "rgba(0,0,0,0.04)", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.1) } }}
              />
            ))}
            {overflow > 0 && (
              <Chip size="small" label={`+${overflow} more`}
                sx={{ height: 20, fontSize: 10.5, fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main }} />
            )}
          </>
        )}
      </Box>

      {/* Capacity */}
      <Box sx={{ minWidth: 0, textAlign: { xs: "left", md: "right" } }}>
        <Typography sx={{ fontSize: 13, fontWeight: 800, color: capacityColor, letterSpacing: "-0.01em", whiteSpace: "nowrap" }}>
          {capacityLabel}
        </Typography>
        {isFull && !overCapacity && (
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#f59e0b", letterSpacing: 0.3 }}>FULL</Typography>
        )}
        {overCapacity && (
          <Tooltip title="Enrolled students exceed the configured capacity — please review the class.">
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: "#dc2626", letterSpacing: 0.3, cursor: "help" }}>OVER</Typography>
          </Tooltip>
        )}
      </Box>

      {/* Actions — View (primary) + Edit + kebab */}
      <Stack direction="row" spacing={0.5} onClick={(e) => e.stopPropagation()}>
        <Tooltip title="View class">
          <IconButton size="small" onClick={onViewClass} sx={{ color: theme.palette.primary.main }}>
            <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
        {onEditSchedule && (
          <Tooltip title="Edit schedule">
            <IconButton size="small" onClick={onEditSchedule}>
              <EditOutlinedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        )}
        <Tooltip title="More actions">
          <IconButton size="small" onClick={(e) => onOpenMenu(e.currentTarget)}>
            <MoreVertIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Tooltip>
      </Stack>
    </Box>
  );
};

const ScheduleCard: React.FC<{
  pattern: SchedulePattern;
  onViewClass: () => void;
  onViewNextSession?: () => void;
  onViewSessions?: () => void;
  onEditSchedule?: () => void;
  onAddStudent?: () => void;
  onReleaseStudent?: () => void;
  onSwitchStaff?: () => void;
  onStudentClick: (studentId: number) => void;
  showStaff?: boolean;
}> = ({ pattern, onViewClass, onViewNextSession, onViewSessions, onEditSchedule, onAddStudent, onReleaseStudent, onSwitchStaff, onStudentClick, showStaff }) => {
  const theme = useTheme();
  const [expanded, setExpanded] = useState(false);
  const statusColor = STATUS_COLORS[pattern.status] || "#6b7280";
  const dayLabel = DAY_LABELS[pattern.day.toLowerCase()] || pattern.day;
  const capacityText = pattern.capacity > 0
    ? `${pattern.enrolled}/${pattern.capacity} students`
    : `${pattern.enrolled} student${pattern.enrolled === 1 ? "" : "s"} · Capacity not set`;
  const seatsText = pattern.capacity > 0 && pattern.available !== null
    ? `${pattern.available} seat${pattern.available === 1 ? "" : "s"} available`
    : "";
  const displayStudents = expanded ? pattern.students : pattern.students.slice(0, 4);
  const overflow = pattern.students.length - displayStudents.length;

  // Subtle accent colour driven by the class location (falls back to the
  // Step Up primary green) so cards inherit the same colour language as the
  // Location Colours legend.
  const accent = (pattern.location as any)?.location_color || theme.palette.primary.main;

  return (
    <Box sx={{
      position: "relative",
      bgcolor: "#fff", borderRadius: "14px", border: "1px solid rgba(15,23,42,0.08)",
      p: 2, pl: 2.25, display: "flex", flexDirection: "column", gap: 1.25,
      overflow: "hidden",
      boxShadow: "0 1px 2px rgba(15,23,42,0.03)",
      transition: "box-shadow .2s ease, border-color .2s ease, transform .2s ease",
      "&::before": {
        content: '""', position: "absolute", left: 0, top: 0, bottom: 0, width: 4,
        background: `linear-gradient(180deg, ${accent} 0%, ${alpha(accent, 0.45)} 100%)`,
      },
      "&:hover": {
        boxShadow: `0 12px 28px -14px ${alpha(accent, 0.35)}`,
        borderColor: alpha(accent, 0.35),
        transform: "translateY(-1px)",
      },
    }}>
      {/* Header — day/time on the left, elegant status pill on the right */}
      <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.disabled", textTransform: "uppercase", letterSpacing: 0.4 }}>
            {dayLabel}
          </Typography>
          <Typography sx={{ fontSize: 17, fontWeight: 800, color: theme.palette.primary.main, letterSpacing: "-0.01em", lineHeight: 1.15 }}>
            {fmtTime12(pattern.start_time)} – {fmtTime12(pattern.end_time)}
          </Typography>
        </Box>
        <Chip
          size="small"
          label={pattern.status.charAt(0).toUpperCase() + pattern.status.slice(1)}
          icon={<Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: statusColor, ml: "6px !important" }} />}
          sx={{
            height: 22, fontSize: 10.5, fontWeight: 700,
            bgcolor: alpha(statusColor, 0.1), color: statusColor,
            border: `1px solid ${alpha(statusColor, 0.25)}`,
            "& .MuiChip-icon": { color: statusColor },
          }}
        />
      </Stack>

      {/* Grade → Subjects (spec §6) */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
        {pattern.grade_subjects.length === 0 ? (
          <Typography sx={{ fontSize: 12, color: "text.disabled", fontStyle: "italic" }}>No grade/subject configured</Typography>
        ) : pattern.grade_subjects.map((g) => (
          <Box key={g.grade_id} sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 0.5 }}>
            <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: "text.secondary", minWidth: 60 }}>
              {g.grade}
            </Typography>
            {g.subjects.map((s) => (
              <Chip key={s.id} label={s.name} size="small"
                sx={{ height: 20, fontSize: 10.5, fontWeight: 600, bgcolor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main }} />
            ))}
          </Box>
        ))}
      </Box>

      {/* Assigned staff (global page only) */}
      {showStaff && pattern.staff && (
        <Stack direction="row" alignItems="center" spacing={0.75}>
          <PersonOutlineIcon sx={{ fontSize: 14, color: "text.disabled" }} />
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.primary" }}>{pattern.staff.name}</Typography>
        </Stack>
      )}

      {/* Details row */}
      <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap", rowGap: 0.5 }}>
        {pattern.class_type && (
          <MetaItem icon={<CategoryOutlinedIcon />} text={`${pattern.class_type}${pattern.recurring ? " · Recurring" : " · One-off"}`} />
        )}
        {pattern.study_mode && (
          <MetaItem icon={<LaptopMacOutlinedIcon />}
            text={pattern.study_mode + (pattern.location?.name && pattern.study_mode !== "Online" ? ` · ${pattern.location.name}` : "")} />
        )}
        {(!pattern.study_mode && pattern.location?.name) && (
          <MetaItem icon={<PlaceOutlinedIcon />} text={pattern.location.name} />
        )}
        {pattern.program && <MetaItem icon={<EventNoteIcon />} text={pattern.program} />}
      </Stack>

      <Divider sx={{ my: 0.25 }} />

      {/* Enrollment (spec §8) */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ flexWrap: "wrap" }}>
        <GroupsIcon sx={{ fontSize: 14, color: "text.disabled" }} />
        <Typography sx={{ fontSize: 12, fontWeight: 700, color: "text.primary" }}>{capacityText}</Typography>
        {seatsText && <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>· {seatsText}</Typography>}
      </Stack>

      {/* Students (spec §7) */}
      {pattern.students.length > 0 && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, flexWrap: "wrap" }}>
            <AvatarGroup max={5} sx={{ "& .MuiAvatar-root": { width: 22, height: 22, fontSize: 10, fontWeight: 700, borderColor: "#fff" } }}>
              {pattern.students.map((s) => (
                <Avatar key={s.id} src={s.image || undefined}>
                  {s.name.charAt(0).toUpperCase()}
                </Avatar>
              ))}
            </AvatarGroup>
          </Box>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {displayStudents.map((s) => (
              <Chip
                key={s.id} size="small" label={s.name} clickable
                onClick={() => onStudentClick(s.id)}
                sx={{ height: 22, fontSize: 11, bgcolor: "rgba(0,0,0,0.04)", "&:hover": { bgcolor: alpha(theme.palette.primary.main, 0.1) } }}
              />
            ))}
            {overflow > 0 && (
              <Chip size="small" label={`+${overflow} more`}
                onClick={() => setExpanded(true)} clickable
                sx={{ height: 22, fontSize: 11, fontWeight: 700, bgcolor: alpha(theme.palette.primary.main, 0.08), color: theme.palette.primary.main }} />
            )}
            {expanded && pattern.students.length > 4 && (
              <Chip size="small" label="Show less" onClick={() => setExpanded(false)} clickable
                sx={{ height: 22, fontSize: 11, bgcolor: "rgba(0,0,0,0.04)" }} />
            )}
          </Box>
        </Box>
      )}

      {/* Actions */}
      <Stack direction="row" spacing={0.75} sx={{ mt: "auto", pt: 0.5, flexWrap: "wrap", rowGap: 0.5 }}>
        <Button size="small" variant="contained" startIcon={<VisibilityOutlinedIcon sx={{ fontSize: "13px !important" }} />}
          onClick={onViewClass}
          sx={{ textTransform: "none", fontSize: 11, height: 28, boxShadow: "none", borderRadius: "7px" }}>
          View Class
        </Button>
        {onViewNextSession && (
          <Button size="small" variant="outlined" startIcon={<EventNoteIcon sx={{ fontSize: "13px !important" }} />}
            onClick={onViewNextSession}
            sx={{ textTransform: "none", fontSize: 11, height: 28, borderRadius: "7px", borderColor: "rgba(0,0,0,0.1)", color: "text.primary" }}>
            View Next Session
          </Button>
        )}
        {onViewSessions && (
          <Button size="small" variant="text" onClick={onViewSessions}
            sx={{ textTransform: "none", fontSize: 11, height: 28, color: "text.secondary" }}>
            View Sessions
          </Button>
        )}
        {onAddStudent && (
          <Button size="small" variant="text" startIcon={<AddIcon sx={{ fontSize: "13px !important" }} />}
            onClick={onAddStudent}
            sx={{ textTransform: "none", fontSize: 11, height: 28, color: theme.palette.primary.main }}>
            Add Student
          </Button>
        )}
        {onReleaseStudent && (
          <Button size="small" variant="text" startIcon={<PersonRemoveOutlinedIcon sx={{ fontSize: "13px !important" }} />}
            onClick={onReleaseStudent}
            sx={{ textTransform: "none", fontSize: 11, height: 28, color: "#ef4444" }}>
            Release Student
          </Button>
        )}
        {onSwitchStaff && (
          <Button size="small" variant="text" startIcon={<SwapHorizIcon sx={{ fontSize: "13px !important" }} />}
            onClick={onSwitchStaff}
            sx={{ textTransform: "none", fontSize: 11, height: 28, color: "text.secondary" }}>
            Switch Staff
          </Button>
        )}
        {onEditSchedule && (
          <Button size="small" variant="text" onClick={onEditSchedule}
            sx={{ textTransform: "none", fontSize: 11, height: 28, color: "text.secondary" }}>
            Edit Schedule
          </Button>
        )}
      </Stack>
    </Box>
  );
};

const MetaItem: React.FC<{ icon: React.ReactNode; text: string }> = ({ icon, text }) => (
  <Stack direction="row" alignItems="center" spacing={0.5}>
    <Box sx={{ color: "text.disabled", display: "flex", "& svg": { fontSize: 13 } }}>{icon}</Box>
    <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>{text}</Typography>
  </Stack>
);

const EmptyState: React.FC<{
  anyFilter: boolean; onClear: () => void; onAdd?: () => void; onViewAll?: () => void;
}> = ({ anyFilter, onClear, onAdd, onViewAll }) => (
  <Box sx={{ textAlign: "center", py: 6, px: 2 }}>
    <ScheduleIcon sx={{ fontSize: 36, color: "text.disabled", mb: 1 }} />
    {anyFilter ? (
      <>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, mb: 0.5 }}>
          No class schedules match the selected filters.
        </Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 2 }}>
          Try relaxing the filters, or clear them to see everything.
        </Typography>
        <Button size="small" variant="contained" onClick={onClear}
          sx={{ textTransform: "none", fontSize: 12, boxShadow: "none", borderRadius: "8px" }}>
          Clear Filters
        </Button>
      </>
    ) : (
      <>
        <Typography sx={{ fontSize: 13.5, fontWeight: 700, mb: 0.5 }}>
          No class schedules found
        </Typography>
        <Typography sx={{ fontSize: 12, color: "text.secondary", mb: 2 }}>
          {/* Component is reused by the general /class-schedules page and the
              staff profile tab. Copy is intentionally context-agnostic so it
              reads correctly in both places. */}
          Create a class schedule or adjust the filters to see matching schedules.
        </Typography>
        <Stack direction="row" spacing={1} justifyContent="center">
          {onAdd && (
            <Button size="small" variant="contained" startIcon={<AddIcon sx={{ fontSize: "14px !important" }} />}
              onClick={onAdd}
              sx={{ textTransform: "none", fontSize: 12, boxShadow: "none", borderRadius: "8px" }}>
              Add Class
            </Button>
          )}
          {onViewAll && (
            <Button size="small" variant="text" onClick={onViewAll}
              sx={{ textTransform: "none", fontSize: 12, color: "text.secondary" }}>
              View All Classes
            </Button>
          )}
        </Stack>
      </>
    )}
  </Box>
);

export default ClassSchedulesTab;
